import React, { useState, useEffect, useCallback } from 'react'
import './SectionNav.css'

/* ─────────────────────────────────────────────────────────────────────────
   SectionNav — fixed vertical side nav on the right.
   Desktop only (hidden < 1024 px). Hidden during hero video section.
   Glass panel background, icons + always-visible labels, Start CTA.
   ───────────────────────────────────────────────────────────────────────── */

/* Inline SVG icons for each section */
const Icons = {
    home: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
    ),
    features: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    ),
    howToUse: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polygon points="10 8 16 12 10 16 10 8"/>
        </svg>
    ),
    faq: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
    ),
    start: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/>
            <path d="M12 5l7 7-7 7"/>
        </svg>
    ),
}

const SECTIONS = [
    { id: 'AG_HERO_TRUE_SECTION', label: 'Home',       icon: Icons.home },
    { id: 'features',             label: 'Features',    icon: Icons.features },
    { id: 'gettingStart',         label: 'Tutorials',   icon: Icons.howToUse },
    { id: 'faqs',                 label: 'FAQ',         icon: Icons.faq },
]

const SectionNav = ({ onStartClick }) => {
    const [activeId, setActiveId] = useState('AG_HERO_TRUE_SECTION')
    const [visible, setVisible]   = useState(false)

    /* ── Active-section detection ── */
    const detectActive = useCallback(() => {
        const midY = window.innerHeight * 0.4

        // Determine hero height to know when to show/hide the nav
        const heroEl = document.getElementById('AG_HERO_TRUE_SECTION')
        const heroBottom = heroEl ? heroEl.getBoundingClientRect().height : 600

        // Hide entirely during the hero section
        setVisible(window.scrollY > heroBottom * 0.6)

        let currentId = SECTIONS[0].id
        for (const { id } of SECTIONS) {
            const el = document.getElementById(id)
            if (!el) continue
            if (el.getBoundingClientRect().top <= midY) {
                currentId = id
            }
        }
        setActiveId(currentId)
    }, [])

    useEffect(() => {
        window.addEventListener('scroll', detectActive, { passive: true })
        detectActive()
        return () => window.removeEventListener('scroll', detectActive)
    }, [detectActive])

    const scrollTo = useCallback((id) => {
        const el = document.getElementById(id)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [])

    return (
        <nav
            className={`snav ${visible ? 'snav--visible' : ''}`}
            aria-label="Page sections"
        >
            <div className="snav__panel">
                {SECTIONS.map(({ id, label, icon }) => (
                    <button
                        key={id}
                        className={`snav__item ${activeId === id ? 'is-active' : ''}`}
                        onClick={() => scrollTo(id)}
                        aria-label={`Go to ${label} section`}
                    >
                        <span className="snav__icon">{icon}</span>
                        <span className="snav__label">{label}</span>
                    </button>
                ))}

                {/* Divider */}
                <div className="snav__divider" />

                {/* Start / CTA button */}
                <button
                    className="snav__start"
                    onClick={() => {
                        if (onStartClick) {
                            onStartClick()
                        } else {
                            // Fallback: scroll to top and trigger any signup popup via DOM
                            window.scrollTo({ top: 0, behavior: 'smooth' })
                        }
                    }}
                    aria-label="Get started"
                >
                    <span className="snav__icon">{Icons.start}</span>
                    <span className="snav__label">Start</span>
                </button>
            </div>
        </nav>
    )
}

export default SectionNav
